import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import EmojiPicker from "emoji-picker-react";
import "../assets/css/AdminChat1.scss";
import {
  getChatUsers,
  getChatMessages,
  sendChatMessage,
} from "../redux/services/chatServices/chat.service";
import { FiRefreshCw, FiLogOut, FiUser } from "react-icons/fi";
const AdminPanel = ({ admin, onLogout }) => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [filePreview, setFilePreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [isConnected, setIsConnected] = useState(true);
  const [currentAdminId, setCurrentAdminId] = useState(null);

  // Polling related
  const pollingIntervalRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const attachmentMenuRef = useRef(null);
  const notificationRef = useRef(null);
  const menuRefs = useRef({});
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const processedMessageIds = useRef(new Set());

  const quickEmojis = [
    "😊",
    "😂",
    "🥰",
    "😍",
    "😎",
    "👍",
    "❤️",
    "🔥",
    "🎉",
    "🙏",
  ];

  // Get admin ID from localStorage or admin prop
  useEffect(() => {
    const storedAdmin = localStorage.getItem("admin");
    if (storedAdmin) {
      try {
        const adminData = JSON.parse(storedAdmin);
        setCurrentAdminId(adminData.id);
      } catch (e) {}
    }
    if (admin?.id) {
      setCurrentAdminId(admin.id);
    }
    if (!currentAdminId) {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split(".")[1]));
          setCurrentAdminId(payload.id || payload.userId || payload.sub);
        } catch (e) {}
      }
    }
  }, [admin]);

  // Load users on mount
  useEffect(() => {
    loadUsers();

    return () => {
      stopPolling();
    };
  }, []);

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
      if (
        attachmentMenuRef.current &&
        !attachmentMenuRef.current.contains(event.target)
      ) {
        setShowAttachmentMenu(false);
      }
      Object.values(menuRefs.current).forEach((ref) => {
        if (ref && !ref.contains(event.target)) {
          setActiveMenu(null);
        }
      });
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const playNotificationSound = () => {};

  // Load Users
  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await getChatUsers();
      console.log("Users API response:", response);
      if (response.success) {
        setUsers(response.data);
      }
    } catch (error) {
      console.error("Error loading users:", error);
    } finally {
      setLoading(false);
    }
  };

  // Polling - Check for new messages
  const startPolling = (userId) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(async () => {
      if (!selectedUser) return;

      try {
        const response = await getChatMessages(selectedUser.id);

        if (response.success && response.data) {
          const newMessages = response.data;

          newMessages.forEach((msg) => {
            if (!processedMessageIds.current.has(msg.id)) {
              processedMessageIds.current.add(msg.id);

              setMessages((prev) => [...prev, msg]);

              if (msg.sender_id !== currentAdminId) {
                setUsers((prev) =>
                  prev.map((user) =>
                    user.id === msg.sender_id
                      ? {
                          ...user,
                          unread_count: (user.unread_count || 0) + 1,
                          last_message: msg.message,
                        }
                      : user,
                  ),
                );
              }
            }
          });

          setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
          }, 100);
        }
      } catch (error) {
        console.error("Polling error:", error);
      }
    }, 3000);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // Load User Messages
  const loadUserMessages = async (user) => {
    if (!user || !user.id) {
      console.error("No user or user.id provided");
      return;
    }

    console.log("Loading messages for user ID:", user.id);
    console.log("Current admin ID:", currentAdminId);

    stopPolling();

    try {
      setLoading(true);
      const response = await getChatMessages(user.id);
      console.log("Messages API response:", response);

      if (response.success) {
        processedMessageIds.current.clear();

        const messagesData = response.data || [];
        console.log(`Loaded ${messagesData.length} messages`);

        messagesData.forEach((msg) => {
          processedMessageIds.current.add(msg.id);
        });

        setMessages(messagesData);
        setSelectedUser(user);
        setEditingMessage(null);
        setActiveMenu(null);

        setUsers((prev) =>
          prev.map((u) => (u.id === user.id ? { ...u, unread_count: 0 } : u)),
        );

        startPolling(user.id);

        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else {
        console.error("Failed to load messages:", response.message);
      }
    } catch (error) {
      console.error("Error loading messages:", error);
    } finally {
      setLoading(false);
    }
  };

  // Send Text Message
  const sendTextMessage = async () => {
    if (!newMessage.trim() || !selectedUser) return;

    const messageText = newMessage;
    setNewMessage("");

    try {
      const result = await sendChatMessage(selectedUser.id, messageText);
      console.log("Send message response:", result);

      if (result.success) {
        await loadUserMessages(selectedUser);
        await loadUsers();
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else {
        alert("Failed to send message: " + (result.message || "Unknown error"));
      }
    } catch (error) {
      console.error("Error sending message:", error);
      alert("Failed to send message");
    }
  };

  // Edit Message
  const editMessage = async (messageId, newContent) => {
    try {
      const response = await axios.put(
        `${baseUrl}/api/messages/${messageId}`,
        {
          message: newContent,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (response.data.success) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId
              ? { ...msg, message: newContent, isEdited: true }
              : msg,
          ),
        );
      }
      setEditingMessage(null);
      setActiveMenu(null);
    } catch (error) {
      console.error("Error editing message:", error);
      alert("Error editing message");
    }
  };

  // Delete Message
  const deleteMessage = async (messageId) => {
    if (!window.confirm("Are you sure you want to delete this message?")) {
      return;
    }

    try {
      const response = await axios.delete(
        `${baseUrl}/api/messages/${messageId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (response.data.success) {
        setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
        processedMessageIds.current.delete(messageId);
      }
      setActiveMenu(null);
    } catch (error) {
      console.error("Error deleting message:", error);
      alert("Error deleting message");
    }
  };

  // Start Edit
  const startEdit = (message) => {
    if (message.message_type === "support") {
      setEditingMessage(message);
      setNewMessage(message.message);
      setActiveMenu(null);
    }
  };

  // Cancel Edit
  const cancelEdit = () => {
    setEditingMessage(null);
    setNewMessage("");
  };

  // Toggle Menu
  const toggleMenu = (messageId) => {
    setActiveMenu(activeMenu === messageId ? null : messageId);
  };

  // Handle Send
  const handleSend = async () => {
    if ((!newMessage.trim() && !selectedFile) || !selectedUser) return;

    if (editingMessage) {
      await editMessage(editingMessage.id, newMessage.trim());
      setNewMessage("");
    } else {
      if (selectedFile) {
        await sendFileMessage();
      } else {
        await sendTextMessage();
      }
    }
  };

  // Send File Message
  const sendFileMessage = async () => {
    if (!selectedFile || !selectedUser) return;

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("receiver_id", selectedUser.id);

    try {
      setUploading(true);
      const response = await axios.post(
        `${baseUrl}/api/chat-upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (response.data.success) {
        await loadUserMessages(selectedUser);
        cancelFileSelection();
        setNewMessage("");
      }
    } catch (error) {
      alert("Error uploading file");
    } finally {
      setUploading(false);
    }
  };

  // File Select
  const handleFileSelect = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert("File size too large. Maximum size is 10MB.");
        return;
      }

      setSelectedFile(file);
      setFileType(type);

      if (type === "image") {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFilePreview(e.target.result);
        };
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }

      setShowAttachmentMenu(false);
    }
    e.target.value = "";
  };

  // Cancel File Selection
  const cancelFileSelection = () => {
    setSelectedFile(null);
    setFilePreview(null);
    setFileType(null);
  };

  // Key Press
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Emoji handlers
  const onEmojiClick = (emojiObject) => {
    setNewMessage((prev) => prev + emojiObject.emoji);
    setShowEmojiPicker(false);
  };

  const addQuickEmoji = (emoji) => {
    setNewMessage((prev) => prev + emoji);
    setShowEmojiPicker(false);
  };

  const toggleEmojiPicker = () => {
    setShowEmojiPicker(!showEmojiPicker);
    setShowAttachmentMenu(false);
  };

  const toggleAttachmentMenu = () => {
    setShowAttachmentMenu(!showAttachmentMenu);
    setShowEmojiPicker(false);
  };

  // ✅ CRITICAL: Function to determine if message is sent by admin
  const isSentByAdmin = (msg) => {
    // Check by sender_id
    if (currentAdminId && msg.sender_id === currentAdminId) {
      return true;
    }
    // Check by message_type
    if (msg.message_type === "support" || msg.sender_type === "admin") {
      return true;
    }
    // Check if sender is not the selected user (fallback)
    if (selectedUser && msg.sender_id !== selectedUser.id) {
      return true;
    }
    return false;
  };

  // Filtered Users
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.id?.toString().includes(searchTerm) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "online" && user.status === "active") ||
      (statusFilter === "offline" && user.status !== "active");

    return matchesSearch && matchesStatus;
  });

  // Stats
  const stats = {
    total: users.length,
    online: users.filter((u) => u.status === "active").length,
    offline: users.filter((u) => u.status !== "active").length,
    totalMessages: messages.length,
  };

  // Format Time
  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date >= today) {
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (date >= yesterday) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }
  };

  // Status Badge
  const getStatusBadge = (status) => {
    const isOnline = status === "active";
    return (
      <span className={`status-badge ${isOnline ? "online" : "offline"}`}>
        {isOnline ? "Online" : "Offline"}
      </span>
    );
  };

  // Render Message Content
  const renderMessageContent = (msg) => {
    return (
      <div className="message-text">
        {msg.message}
        {msg.isEdited && <span className="edited-indicator"> edited</span>}
      </div>
    );
  };

  // File Preview
  const renderFilePreview = () => {
    if (!selectedFile) return null;

    return (
      <div className="file-preview-container">
        <div className="file-preview-header">
          <span className="file-preview-title">
            {fileType === "image" ? "Image Preview" : "Document Preview"}
          </span>
          <button className="file-preview-close" onClick={cancelFileSelection}>
            ×
          </button>
        </div>
        <div className="file-preview-body">
          {fileType === "image" ? (
            <img
              src={filePreview}
              alt="Preview"
              className="file-preview-image"
            />
          ) : (
            <div className="file-preview-doc">
              <div className="doc-icon-large">📄</div>
              <div className="doc-details">
                <div className="doc-name">{selectedFile.name}</div>
                <div className="doc-size">
                  {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="whatsapp-container">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="bg_chat_box">
          <div className="sidebar-header">
            <div className="admin-avatar">
              <span>
                <FiUser />
              </span>
            </div>
            <div className="admin-info-header">
              <div className="admin-name-header">Admin Portal</div>
              <div className="admin-status">Online</div>
            </div>
            {/* <div className="header-icons">
            <button onClick={loadUsers} className="icon-btn" disabled={loading}>
              🔄
            </button>
            <button onClick={onLogout} className="icon-btn">
              🚪
            </button>
          </div> */}
            <div className="header-icons">
              <button
                onClick={loadUsers}
                className="icon-btn"
                disabled={loading}
                title="Refresh"
              >
                <FiRefreshCw className={loading ? "spin-icon" : ""} size={18} />
              </button>

              <button
                onClick={onLogout}
                className="icon-btn logout-btn"
                title="Logout"
              >
                <FiLogOut size={18} />
              </button>
            </div>
          </div>

          <div className="search-bar">
            <div className="search-icon">🔍</div>
            <input
              type="text"
              placeholder="Search or start new chat"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-tabs">
            <button
              className={`filter-tab ${statusFilter === "all" ? "active" : ""}`}
              onClick={() => setStatusFilter("all")}
            >
              All
            </button>
            <button
              className={`filter-tab ${statusFilter === "online" ? "active" : ""}`}
              onClick={() => setStatusFilter("online")}
            >
              Online
            </button>
            <button
              className={`filter-tab ${statusFilter === "offline" ? "active" : ""}`}
              onClick={() => setStatusFilter("offline")}
            >
              Offline
            </button>
          </div>
        </div>
        <div className="chat-list">
          {loading ? (
            <div className="loading-state">Loading chats...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="empty-state">No chats found</div>
          ) : (
            filteredUsers.map((user) => (
              <div
                key={user.id}
                className={`chat-item ${selectedUser?.id === user.id ? "active" : ""}`}
                onClick={() => loadUserMessages(user)}
              >
                <div className="chat-avatar">
                  <span>👤</span>
                  {user.status === "active" && (
                    <span className="online-indicator"></span>
                  )}
                </div>
                <div className="chat-info">
                  <div className="chat-name">{user.name || user.email}</div>
                  <div className="chat-last-message">
                    {user.last_message || "No messages yet"}
                  </div>
                </div>
                <div className="chat-meta">
                  <div className="chat-time">
                    {user.last_message_time
                      ? formatTime(user.last_message_time)
                      : ""}
                  </div>
                  {user.unread_count > 0 && (
                    <div className="unread-count">{user.unread_count}</div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="sidebar-footer">
          <div className="stats-badge">
            <span>Total: {stats.total}</span>
            <span>Online: {stats.online}</span>
          </div>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="chat-area">
        {selectedUser ? (
          <>
            <div className="chat-header">
              <div className="chat-header-info">
                <div className="chat-avatar-large">
                  <span>👤</span>
                  {selectedUser.status === "active" && (
                    <span className="online-indicator-large"></span>
                  )}
                </div>
                <div className="chat-header-details">
                  <div className="chat-header-name">
                    {selectedUser.name || selectedUser.email}
                  </div>
                  <div className="chat-header-status">
                    {selectedUser.status === "active" ? "Online" : "Offline"}
                  </div>
                </div>
              </div>
              <div className="chat-header-actions">
                <button
                  className="icon-btn"
                  onClick={() => loadUserMessages(selectedUser)}
                >
                  🔄
                </button>
              </div>
            </div>

            {/* Messages Area - WhatsApp Style */}
            <div className="messages-area">
              {messages.length === 0 ? (
                <div className="empty-messages">
                  <div className="empty-messages-icon">💬</div>
                  <p>No messages yet</p>
                  <span>Send a message to start the conversation</span>
                </div>
              ) : (
                <div className="messages-list">
                  {messages.map((msg, index) => {
                    const showDate =
                      index === 0 ||
                      new Date(msg.created_at).toDateString() !==
                        new Date(
                          messages[index - 1]?.created_at,
                        ).toDateString();
                    // ✅ Determine alignment based on sender
                    const isAdminMsg = isSentByAdmin(msg);

                    return (
                      <React.Fragment key={msg.id}>
                        {showDate && (
                          <div className="date-divider">
                            <span>
                              {new Date(msg.created_at).toLocaleDateString(
                                "en-US",
                                {
                                  weekday: "long",
                                  month: "long",
                                  day: "numeric",
                                },
                              )}
                            </span>
                          </div>
                        )}
                        {/* ✅ WhatsApp Style: Admin messages on RIGHT, User messages on LEFT */}
                        <div
                          className={`message-wrapper ${isAdminMsg ? "admin-message" : "user-message"}`}
                        >
                          <div
                            className={`message-bubble ${isAdminMsg ? "admin-bubble" : "user-bubble"}`}
                          >
                            <div className="message-text-content">
                              {renderMessageContent(msg)}
                            </div>
                            <div className="message-meta">
                              <span className="message-time">
                                {formatTime(msg.created_at)}
                              </span>
                              {isAdminMsg && (
                                <span className="message-status-icon">
                                  {msg.is_read ? "✓✓" : "✓"}
                                </span>
                              )}
                            </div>
                          </div>
                          {isAdminMsg && (
                            <div className="message-actions-dropdown">
                              <button
                                className="message-menu-btn"
                                onClick={() => toggleMenu(msg.id)}
                              >
                                ⋮
                              </button>
                              {activeMenu === msg.id && (
                                <div
                                  className="message-menu"
                                  ref={(el) => (menuRefs.current[msg.id] = el)}
                                >
                                  <button
                                    className="menu-item"
                                    onClick={() => startEdit(msg)}
                                  >
                                    ✏️ Edit
                                  </button>
                                  <button
                                    className="menu-item delete"
                                    onClick={() => deleteMessage(msg.id)}
                                  >
                                    🗑️ Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {editingMessage && (
              <div className="edit-indicator-bar">
                <span>✏️ Editing message</span>
                <button className="cancel-edit" onClick={cancelEdit}>
                  Cancel
                </button>
              </div>
            )}

            {selectedFile && renderFilePreview()}

            <div className="input-area">
              {showEmojiPicker && (
                <div className="emoji-picker-wrapper" ref={emojiPickerRef}>
                  <div className="emoji-picker-header">
                    <span>Emojis</span>
                    <button
                      className="close-picker"
                      onClick={() => setShowEmojiPicker(false)}
                    >
                      ×
                    </button>
                  </div>
                  <div className="emoji-picker-content">
                    <EmojiPicker
                      onEmojiClick={onEmojiClick}
                      height={350}
                      width="100%"
                    />
                  </div>
                  <div className="quick-emojis">
                    {quickEmojis.map((emoji, index) => (
                      <button
                        key={index}
                        className="quick-emoji"
                        onClick={() => addQuickEmoji(emoji)}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {showAttachmentMenu && (
                <div className="attachment-menu" ref={attachmentMenuRef}>
                  <button
                    className="attachment-option"
                    onClick={() => imageInputRef.current?.click()}
                  >
                    <span className="attachment-icon">📷</span>
                    <span>Gallery</span>
                  </button>
                  <button
                    className="attachment-option"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <span className="attachment-icon">📄</span>
                    <span>Document</span>
                  </button>
                </div>
              )}

              <div className="input-container">
                <button className="input-icon" onClick={toggleEmojiPicker}>
                  😊
                </button>
                <button className="input-icon" onClick={toggleAttachmentMenu}>
                  📎
                </button>
                <input
                  type="file"
                  ref={imageInputRef}
                  style={{ display: "none" }}
                  onChange={(e) => handleFileSelect(e, "image")}
                  accept="image/*"
                />
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={(e) => handleFileSelect(e, "file")}
                  accept="*/*"
                />
                <div className="message-input-wrapper">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={
                      editingMessage
                        ? "Edit your message..."
                        : selectedFile
                          ? "Add a caption..."
                          : "Type a message"
                    }
                    className="message-input-field"
                    disabled={uploading}
                  />
                </div>
                <button
                  onClick={handleSend}
                  className="send-message-btn"
                  disabled={(!newMessage.trim() && !selectedFile) || uploading}
                >
                  {uploading ? (
                    <div className="send-spinner"></div>
                  ) : (
                    <svg
                      viewBox="0 0 24 24"
                      width="24"
                      height="24"
                      fill="currentColor"
                    >
                      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="welcome-screen">
            <div className="welcome-icon-large">💬</div>
            <h2>WhatsApp Admin Panel</h2>
            <p>Select a conversation to start chatting</p>
          </div>
        )}
      </div>

      {selectedImage && (
        <div
          className="image-modal-overlay"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="image-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="modal-close"
              onClick={() => setSelectedImage(null)}
            >
              ×
            </button>
            <img src={selectedImage} alt="Zoomed" className="modal-image" />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
